import { useEffect, useState, type ReactNode } from 'react';
import { ErrorState, LoadingState } from '../components/states.js';
import {
  Badge,
  Button,
  Card,
  IconButton,
  DataRow,
  Notice,
  PageHeader,
  SectionTitle,
} from '../components/ui.js';
import { IconDownload, IconRefresh } from '../components/icons.js';
import { update, type StavAktualizacie } from '../api/client.js';
import { useSession } from '../auth/session.js';
import { SpravaUctov, ZmenaHesla } from './ucty.js';

/*
 * Systém.
 *
 * Cita `/readyz`, ktore je bez autentifikacie - je urcene orchestratoru.
 * Preto sa tu nezobrazuju ziadne podrobnosti o chybach integracii; tie
 * su na obrazovke Integracie, ktora je za prihlasenim.
 */

interface IntegrationRow {
  readonly id: string;
  readonly state: string;
  readonly deviceCount: number;
}

interface ReadyzResponse {
  readonly status: 'ok' | 'degraded' | 'failed';
  readonly uptimeSeconds: number;
  readonly deviceCount: number;
  readonly integrations: readonly IntegrationRow[];
}

type Stav =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly detail: string }
  | { readonly kind: 'ready'; readonly data: ReadyzResponse };

const POPIS_STAVU: Record<ReadyzResponse['status'], string> = {
  ok: 'Všetko beží',
  degraded: 'Časť integrácií nebeží',
  failed: 'Nebeží žiadna integrácia',
};

const TON_STAVU: Record<ReadyzResponse['status'], 'ok' | 'warn' | 'danger'> = {
  ok: 'ok',
  degraded: 'warn',
  failed: 'danger',
};

/** Cas behu v podobe, ktora sa da precitat bez pocitania. */
function trvanie(sekundy: number): string {
  const dni = Math.floor(sekundy / 86400);
  const hodiny = Math.floor((sekundy % 86400) / 3600);
  const minuty = Math.floor((sekundy % 3600) / 60);
  if (dni > 0) return `${dni} d ${hodiny} h`;
  if (hodiny > 0) return `${hodiny} h ${minuty} min`;
  if (minuty > 0) return `${minuty} min`;
  return `${sekundy} s`;
}

export function System(): ReactNode {
  const { user } = useSession();
  const [stav, setStav] = useState<Stav>({ kind: 'loading' });

  const nacitaj = (): void => {
    setStav({ kind: 'loading' });
    fetch('/readyz')
      .then(async (r) => {
        // 503 je platna odpoved so spravnym telom, nie chyba prenosu.
        if (!r.ok && r.status !== 503) throw new Error(`stav ${r.status}`);
        return (await r.json()) as ReadyzResponse;
      })
      .then((data) => {
        setStav({ kind: 'ready', data });
      })
      .catch(() => {
        // Pouzivatelovi zrozumitelna sprava BEZ technickeho detailu.
        setStav({ kind: 'error', detail: 'Server neodpovedá. Skontroluj, či LoxLink beží.' });
      });
  };

  useEffect(nacitaj, []);

  const bezTls = typeof window !== 'undefined' && window.location.protocol !== 'https:';

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Systém" detail="Stav behu a nastavenie tejto brány." />

      {bezTls && (
        <Notice tone="warn">
          <p className="font-medium">Spojenie nie je šifrované.</p>
          <p className="mt-1">
            Prihlasovacia relácia ide po sieti nechránená a cookie nemá príznak <code>secure</code>.
            V domácej sieti je to vedomý kompromis; pri prístupe zvonku nastav HTTPS alebo reverznú
            proxy.
          </p>
        </Notice>
      )}

      {stav.kind === 'loading' && <LoadingState what="Zisťujem stav servera" />}
      {stav.kind === 'error' && <ErrorState detail={stav.detail} onRetry={nacitaj} />}

      {stav.kind === 'ready' && (
        <div className="flex flex-col gap-6">
          <Card className="flex flex-col gap-4 p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
              <SectionTitle>Stav brány</SectionTitle>
              <Badge tone={TON_STAVU[stav.data.status]}>{POPIS_STAVU[stav.data.status]}</Badge>
            </div>
            <dl>
              <DataRow label="Čas behu">{trvanie(stav.data.uptimeSeconds)}</DataRow>
              <DataRow label="Zariadenia">{stav.data.deviceCount}</DataRow>
              <DataRow label="Integrácie">{stav.data.integrations.length}</DataRow>
            </dl>
          </Card>

          <section className="flex flex-col gap-3">
            <SectionTitle>Prihlásený účet</SectionTitle>
            <Card className="px-4 py-1">
              <dl>
                <DataRow label="Meno">{user?.username ?? 'neznámy'}</DataRow>
                <DataRow label="Rola">{user?.role === 'admin' ? 'Správca' : 'Len čítanie'}</DataRow>
                <DataRow label="Posledné prihlásenie">
                  {user?.lastLoginAt === null || user?.lastLoginAt === undefined
                    ? 'toto je prvé'
                    : new Date(user.lastLoginAt).toLocaleString('sk-SK')}
                </DataRow>
              </dl>
            </Card>
          </section>

          <Aktualizacia />
          <ZmenaHesla />
          <SpravaUctov />
        </div>
      )}
    </div>
  );
}

/*
 * Aktualizacia brany.
 *
 * LoxLink sa neaktualizuje sam - bezi v kontajneri bez pristupu
 * k Dockeru. Tlacidlo len zapise poziadavku, ktoru vykona agent na
 * hostitelovi. Preto sa karta neponuka, kym sa agent neohlasi: slubit
 * tlacidlo, ktore nema kto obsluzit, by bola lož.
 *
 * Pocas aktualizacie sa brana restartuje, takze dotazy prestanu chodit.
 * Vypadok NIE JE chyba - stav sa dalej zistuje, kym sa brana neozve.
 */

const POPIS_FAZY: Record<string, string> = {
  priprava: 'Pripravujem',
  zaloha: 'Zálohujem',
  zostavenie: 'Zostavujem obraz',
  restart: 'Reštartujem',
  cakanie: 'Čakám, kým brána nabehne',
};

const INTERVAL_POKOJ_MS = 60_000;
/** Kolko krat a ako casto sa po vyziadani pyta na vysledok kontroly. */
const POKUSOV_O_KONTROLU = 10;
const PAUZA_KONTROLY_MS = 2000;
const INTERVAL_PRIEBEH_MS = 5_000;

function Aktualizacia(): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const [stav, setStav] = useState<StavAktualizacie | undefined>(undefined);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [ziada, setZiada] = useState(false);
  const [kontroluje, setKontroluje] = useState(false);
  const [neodpoveda, setNeodpoveda] = useState(false);

  const prebieha = stav?.kind === 'znamy' && stav.prebieha !== null;

  useEffect(() => {
    let zivy = true;
    const nacitaj = (): void => {
      update
        .status()
        .then(({ update: u }) => {
          if (!zivy) return;
          setStav(u);
          setNeodpoveda(false);
        })
        .catch(() => {
          /*
           * Pocas restartu brana neodpoveda. Nie je to chyba - ale ani
           * sa to nesmie tvarit, ze sa nic nedeje: bez tohto priznaku
           * zostal na obrazovke posledny znamy krok a vyzeralo to,
           * ze priebeh zamrzol.
           */
          if (zivy) setNeodpoveda(true);
        });
    };
    nacitaj();
    const timer = setInterval(nacitaj, prebieha ? INTERVAL_PRIEBEH_MS : INTERVAL_POKOJ_MS);
    return () => {
      zivy = false;
      clearInterval(timer);
    };
  }, [prebieha]);

  // Kym sa agent neohlasi, karta sa vobec neukazuje.
  /*
   * Kontrola na poziadanie.
   *
   * Casovac na hostitelovi bezi raz za hodinu. Bez tohto tlacidla by sa
   * po vydani novej verzie cakalo az do jeho dalsieho behu - a keby
   * kontrola zlyhavala, rozhranie by dovtedy tvrdilo, ze bezi najnovsia
   * verzia.
   */
  const skontroluj = async (): Promise<void> => {
    setChyba(undefined);
    setKontroluje(true);
    // Necitatelny stav ziadny cas kontroly nema; po prepisani ho uz
    // bude mat, a prave to je znamenie, ze agent zareagoval.
    const predtym = stav?.kind === 'znamy' ? stav.poslednaKontrolaAt : null;

    try {
      await update.check();

      /*
       * Caka sa na to, ze agent zapise NOVY cas kontroly - nie len
       * nejaky cas. Bez toho by tlacidlo po uplynuti pauzy ukazalo
       * povodny stav a tvarilo sa, ze je vsetko v poriadku, aj ked
       * poziadavku nikto nespracoval.
       */
      for (let pokus = 0; pokus < POKUSOV_O_KONTROLU; pokus += 1) {
        await new Promise((r) => setTimeout(r, PAUZA_KONTROLY_MS));
        const { update: novy } = await update.status();
        setStav(novy);
        if (novy.kind === 'znamy' && novy.poslednaKontrolaAt !== predtym) return;
      }

      setChyba(
        'Agent na požiadavku nezareagoval. Skontroluj, či na hostiteľovi beží ' +
          'loxlink-update-check.path — pridáva ho sudo ./install-updater.sh.',
      );
    } catch (error: unknown) {
      setChyba(error instanceof Error ? error.message : 'Kontrolu sa nepodarilo spustiť.');
    } finally {
      setKontroluje(false);
    }
  };

  /*
   * Necitatelny stav sa NESMIE schovat. Karta zmizne len vtedy, ked
   * agent nie je nainstalovany - vtedy je to vedoma volba spravcu.
   */
  if (stav?.kind === 'nezrozumitelny') {
    return (
      <section className="flex flex-col gap-3">
        <SectionTitle>Aktualizácia</SectionTitle>
        <Card className="flex flex-col gap-3 p-4 sm:p-5">
          <Notice tone="warn">
            Agent aktualizácie zapísal stav, ktorému brána nerozumie, takže sa nedá povedať, či je
            nová verzia. Podrobnosť je v logu brány; najčastejšia príčina je zlyhaný prístup k
            repozitáru.
          </Notice>
          {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
          {jeSpravca && (
            <div>
              <Button
                icon={<IconRefresh className="size-4" />}
                disabled={kontroluje}
                onClick={() => {
                  void skontroluj();
                }}
              >
                {kontroluje ? 'Kontrolujem…' : 'Skontrolovať znova'}
              </Button>
            </div>
          )}
        </Card>
      </section>
    );
  }

  if (stav === undefined || stav.kind !== 'znamy') return null;

  const spusti = (): void => {
    setChyba(undefined);
    setZiada(true);
    update
      .start()
      .then(() => {
        setStav({
          ...stav,
          prebieha: { id: '', faza: 'priprava', zacalaAt: new Date().toISOString() },
        });
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Aktualizáciu sa nepodarilo spustiť.');
      })
      .finally(() => setZiada(false));
  };

  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Aktualizácia</SectionTitle>
      <Card className="flex flex-col gap-4 p-4 sm:p-5">
        <dl>
          <DataRow label="Bežiaca verzia">{stav.beziacaVerzia}</DataRow>
          <DataRow label="Dostupná verzia">{stav.dostupnaVerzia ?? 'zatiaľ nezistená'}</DataRow>
          <DataRow label="Posledná kontrola">
            <span className="inline-flex items-center gap-1">
              {kontroluje
                ? 'kontrolujem…'
                : stav.poslednaKontrolaAt === null
                  ? 'zatiaľ nebola'
                  : new Date(stav.poslednaKontrolaAt).toLocaleString('sk-SK')}
              {jeSpravca && stav.prebieha === null && (
                <IconButton
                  label="Skontrolovať dostupnú verziu teraz"
                  icon={<IconRefresh className="size-4" />}
                  disabled={kontroluje}
                  onClick={() => {
                    void skontroluj();
                  }}
                />
              )}
            </span>
          </DataRow>
        </dl>

        {stav.poslednaChyba !== null && (
          <Notice tone="warn">
            <p className="font-medium">Posledná kontrola sa nedostala k repozitáru.</p>
            <p className="mt-1">
              Dovtedy platí naposledy zistená verzia, takže „beží najnovšia" nemusí byť pravda.
            </p>
            <p className="mt-1 font-mono text-xs">{stav.poslednaChyba}</p>
          </Notice>
        )}

        {stav.prebieha !== null ? (
          <Priebeh
            faza={stav.prebieha.faza}
            zacalaAt={stav.prebieha.zacalaAt}
            neodpoveda={neodpoveda}
          />
        ) : stav.jeNovsia ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-(--color-text-muted)">
              Je dostupná novšia verzia. Pred aktualizáciou sa spraví záloha.
            </p>
            {jeSpravca ? (
              <Button
                variant="primary"
                icon={<IconDownload className="size-4" />}
                onClick={spusti}
                disabled={ziada}
              >
                {ziada ? 'Spúšťam…' : `Aktualizovať na ${stav.dostupnaVerzia ?? 'novšiu'}`}
              </Button>
            ) : (
              <Badge tone="neutral">Aktualizovať môže len správca</Badge>
            )}
          </div>
        ) : (
          <p className="flex items-center gap-2 text-sm text-(--color-text-muted)">
            <IconRefresh className="size-4" />
            Beží najnovšia známa verzia.
          </p>
        )}

        {/*
         * Ukazuje sa len ZLYHANIE.
         *
         * Ze aktualizacia presla, hovori bežiaca verzia hned hore - druhy
         * zeleny oznam pod nou uz len opakoval to iste (postreh zadavatela
         * 2026-09-16). Zlyhanie sa naopak inde nedozvie nikto.
         */}
        {stav.poslednyVysledok !== null && !stav.poslednyVysledok.ok && stav.prebieha === null && (
          <Notice tone="danger">
            <p className="font-medium">Posledná aktualizácia zlyhala.</p>
            <p className="mt-1">{stav.poslednyVysledok.sprava}</p>
          </Notice>
        )}

        {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
      </Card>
    </section>
  );
}

/*
 * Priebeh aktualizacie.
 *
 * Zostavenie obrazu trva na Raspberry Pi rádovo desat minut a brana sa
 * medzitym restartuje. Bez viditelneho priebehu vyzera cele ticho ako
 * zamrznutie - preto je vidno, ktory krok prave bezi a ako dlho uz
 * aktualizacia trva.
 */
const FAZY = ['priprava', 'zostavenie', 'zaloha', 'restart', 'cakanie'] as const;

function Priebeh({
  faza,
  zacalaAt,
  neodpoveda,
}: {
  readonly faza: string;
  readonly zacalaAt: string;
  /** Brana prave neodpoveda - takmer isto sa restartuje. */
  readonly neodpoveda: boolean;
}): ReactNode {
  const index = FAZY.indexOf(faza as (typeof FAZY)[number]);
  // Neznama faza (novsi agent) sa nepredstiera ako hotova.
  const hotovych = index === -1 ? 0 : index + 1;
  const podiel = Math.round((hotovych / FAZY.length) * 100);

  const zaciatok = new Date(zacalaAt).getTime();
  const minut = Number.isFinite(zaciatok)
    ? Math.max(0, Math.round((Date.now() - zaciatok) / 60000))
    : 0;

  return (
    <div className="flex flex-col gap-3 rounded-(--radius-control) border border-(--color-warn) bg-(--color-warn-soft) p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-(--color-warn)">
          {POPIS_FAZY[faza] ?? 'Aktualizácia prebieha'}
        </span>
        <span className="font-mono text-xs text-(--color-warn)">
          {hotovych}/{FAZY.length} · {minut} min
        </span>
      </div>

      <div
        role="progressbar"
        aria-label="Priebeh aktualizácie"
        aria-valuemin={0}
        aria-valuemax={FAZY.length}
        aria-valuenow={hotovych}
        aria-valuetext={POPIS_FAZY[faza] ?? faza}
        className="h-2 w-full overflow-hidden rounded-(--radius-pill) bg-(--color-surface-sunken)"
      >
        <div
          className="h-full bg-(--color-warn) transition-[width] duration-500"
          style={{ width: `${podiel}%` }}
        />
      </div>

      <ol className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-(--color-warn)">
        {FAZY.map((f, i) => (
          <li key={f} className={i < hotovych ? 'font-medium' : 'opacity-60'}>
            {i < hotovych - 1 ? '✓ ' : i === hotovych - 1 ? '▸ ' : ''}
            {POPIS_FAZY[f] ?? f}
          </li>
        ))}
      </ol>

      <p className="text-xs text-(--color-warn)">
        {neodpoveda
          ? 'Brána práve neodpovedá — reštartuje sa. Stránku netreba obnovovať, ozve sa sama, keď nabehne.'
          : 'Zostavenie obrazu trvá na Raspberry Pi rádovo desať minút. Brána medzitým beží ďalej; reštartuje sa až na konci.'}
      </p>
    </div>
  );
}
